"use client";

import type { Variants } from "framer-motion";

import {
  Navbar as HeroUINavbar,
  NavbarContent,
  NavbarMenu,
  NavbarMenuToggle,
  NavbarBrand,
  NavbarItem,
  NavbarMenuItem,
} from "@heroui/navbar";
import { Autocomplete, AutocompleteItem } from "@heroui/autocomplete";
import { Avatar } from "@heroui/avatar";
import { Kbd } from "@heroui/kbd";
import { link as linkStyles } from "@heroui/theme";
import NextLink from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useState, useEffect, useSyncExternalStore } from "react";
import clsx from "clsx";
import { isMac } from "@react-aria/utils";

import type { Movie } from "@/types/movie";
import { siteConfig } from "@/config/site";
import { ThemeSwitch } from "@/components/theme-switch";
import { SearchIcon } from "@/components/icons";
import { logError } from "@/lib/logger";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { languageCodes, languages, type LanguageCode } from "@/config/i18n";
import { createTranslator } from "@/lib/i18n/translations";
import { switchLanguageInPath } from "@/lib/i18n/paths";
import { setLanguageCookie } from "@/lib/languageCookie";
import { AuthButton } from "@/components/auth/AuthButton";
import { useAuth } from "@/lib/hooks/useAuth";

interface NavbarProps {
  currentLang: LanguageCode;
}

/** Library pages, all reachable from the "My Library" entry. */
const LIBRARY_PATHS = ["/watchlist", "/watched", "/ratings"] as const;

const noopSubscribe = () => () => {};

/** Large tap targets for the phone menu. */
const menuLinkClass =
  "flex min-h-12 w-full items-center rounded-medium px-2 text-lg outline-none focus-visible:ring-2 focus-visible:ring-focus";

/**
 * The phone menu fades in place instead of HeroUI's default height animation.
 * Without an animated height its top/bottom insets size it to the visible
 * screen (the default `100vh` runs under mobile browser toolbars and cuts off
 * the end of the menu), and links don't slide around while it opens.
 */
const menuMotionProps: { variants: Variants } = {
  variants: {
    enter: { opacity: 1, transition: { duration: 0.2, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.15, ease: "easeIn" } },
  },
};

export const Navbar = ({ currentLang }: NavbarProps) => {
  const t = createTranslator(currentLang);
  const router = useRouter();
  const pathname = usePathname();
  const { status: authStatus } = useAuth();
  // The platform is only known in the browser: render "Ctrl + K" on the
  // server and switch after hydration (no mismatch).
  const isMacPlatform = useSyncExternalStore(noopSubscribe, isMac, () => false);
  // The menu belongs to the page it was opened on: navigating closes it.
  const [menu, setMenu] = useState({ open: false, path: pathname });
  const isMenuOpen = menu.open && menu.path === pathname;
  const setMenuOpen = (open: boolean) => setMenu({ open, path: pathname });
  const [searchQuery, setSearchQuery] = useState("");
  const [search, setSearch] = useState<{ query: string; results: Movie[] }>({
    query: "",
    results: [],
  });
  const trimmedQuery = searchQuery.trim();

  // Debounced search; stale responses are dropped when the query changes.
  useEffect(() => {
    if (!trimmedQuery) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      let results: Movie[] = [];

      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(trimmedQuery)}&lang=${currentLang}`,
          { signal: controller.signal },
        );

        if (response.ok) results = await response.json();
      } catch (error) {
        if (controller.signal.aborted) return;
        logError("Search error in navbar", error, {
          component: "Navbar",
          searchQuery: trimmedQuery,
        });
      }

      if (!controller.signal.aborted) {
        setSearch({ query: trimmedQuery, results });
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [trimmedQuery, currentLang]);

  const isSearching = trimmedQuery !== "" && search.query !== trimmedQuery;
  const searchResults = trimmedQuery ? search.results : [];

  const handleSelectionChange = (key: React.Key | null) => {
    if (key) {
      router.push(`/${currentLang}/movies/${String(key)}`);
      setSearchQuery("");
      setMenuOpen(false);
    }
  };

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const searchInput = document.getElementById("navbar-search");

        searchInput?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const isActive = (href: string) => {
    const path = `/${currentLang}${href === "/" ? "" : href}`;

    return href === "/"
      ? pathname === path
      : pathname === path || pathname.startsWith(`${path}/`);
  };
  const isLibraryActive = LIBRARY_PATHS.some(isActive);
  const showLibrary = authStatus === "authenticated";

  const onNavigate = (href: string) => {
    if (href === "/movies") {
      sessionStorage.removeItem("moviesScrollPosition");
    }
    setMenuOpen(false);
  };

  const switchLanguage = (lang: LanguageCode) => {
    setMenuOpen(false);
    if (lang === currentLang) return;
    setLanguageCookie(lang);
    router.push(
      `${switchLanguageInPath(pathname, lang)}${window.location.search}`,
    );
  };

  const siteLinks = siteConfig.navItems.map((item) => ({
    href: item.href,
    label: t(item.labelKey),
    active: isActive(item.href),
  }));
  const desktopLinks = showLibrary
    ? [
        ...siteLinks,
        {
          href: "/watchlist",
          label: t("nav.myLibrary"),
          active: isLibraryActive,
        },
      ]
    : siteLinks;
  const libraryLinks = LIBRARY_PATHS.map((href) => ({
    href,
    label: t(
      `library.tabs.${href.slice(1) as "watchlist" | "watched" | "ratings"}`,
    ),
    active: isActive(href),
  }));

  const searchInput = (
    <Autocomplete
      aria-label={t("nav.searchLabel")}
      classNames={{
        base: "max-w-md",
        listboxWrapper: "max-h-[400px]",
      }}
      endContent={
        <Kbd className="hidden lg:inline-block">
          {isMacPlatform ? "⌘ + K" : "Ctrl + K"}
        </Kbd>
      }
      id="navbar-search"
      inputValue={searchQuery}
      isLoading={isSearching}
      items={searchResults}
      listboxProps={{
        emptyContent: !trimmedQuery
          ? t("nav.typeToSearch")
          : isSearching
            ? t("common.loading")
            : t("nav.noMoviesFound"),
      }}
      placeholder={t("nav.searchPlaceholder")}
      startContent={<SearchIcon className="text-base text-default-400" />}
      onInputChange={setSearchQuery}
      onSelectionChange={handleSelectionChange}
    >
      {(movie) => (
        <AutocompleteItem
          key={movie.slug}
          startContent={
            <Avatar
              showFallback
              alt={movie.title}
              className="w-10 h-14 rounded-sm"
              fallback={<span className="text-xl">🎬</span>}
              src={movie.posterUrl || undefined}
            />
          }
          textValue={movie.title}
        >
          <div className="flex flex-col">
            <span className="text-sm font-semibold">{movie.title}</span>
            <span className="text-xs text-default-500">
              {movie.releaseYear} • ⭐{" "}
              {movie.rating > 0 ? movie.rating.toFixed(1) : "N/A"}
            </span>
          </div>
        </AutocompleteItem>
      )}
    </Autocomplete>
  );

  const menuToggle = (className: string) => (
    <NavbarMenuToggle
      className={clsx("h-11 w-11 min-w-11", className)}
      srOnlyText={t("nav.toggleMenu")}
    />
  );

  return (
    <HeroUINavbar
      classNames={{
        // Clear of the notch in landscape (the page extends under it)
        wrapper:
          "gap-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] sm:gap-4 sm:pl-[max(1.5rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))]",
      }}
      isMenuOpen={isMenuOpen}
      maxWidth="xl"
      position="sticky"
      onMenuOpenChange={setMenuOpen}
    >
      <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
        <NavbarBrand as="li" className="gap-3 max-w-fit">
          <NextLink
            aria-label="Cage MDB"
            className="flex shrink-0 items-center justify-start gap-2"
            href={`/${currentLang}`}
            onClick={() => setMenuOpen(false)}
          >
            <Image
              alt="Nicolas Cage"
              className="rounded-full"
              height={40}
              src="/nicolas-cage.png"
              width={40}
            />
            {/* The smallest phones only have room for the logo. */}
            <p className="hidden whitespace-nowrap font-bold text-inherit min-[360px]:block">
              Cage MDB
            </p>
          </NextLink>
        </NavbarBrand>
        <ul className="hidden lg:flex gap-4 justify-start ml-2">
          {desktopLinks.map((item) => (
            <NavbarItem key={item.href} isActive={item.active}>
              <NextLink
                aria-current={item.active ? "page" : undefined}
                className={clsx(
                  linkStyles({ color: "foreground" }),
                  "data-[active=true]:text-primary data-[active=true]:font-medium",
                )}
                color="foreground"
                data-active={item.active}
                href={`/${currentLang}${item.href}`}
                onClick={() => onNavigate(item.href)}
              >
                {item.label}
              </NextLink>
            </NavbarItem>
          ))}
        </ul>
      </NavbarContent>

      {/* Tablets and up */}
      <NavbarContent
        className="hidden sm:flex basis-1/5 sm:basis-full"
        justify="end"
      >
        <NavbarItem className="hidden lg:flex">{searchInput}</NavbarItem>
        <NavbarItem className="hidden sm:flex gap-2 items-center">
          <LanguageSwitcher currentLang={currentLang} />
          <ThemeSwitch />
          <AuthButton />
          {/* The links above only fit from `lg` up. */}
          {menuToggle("lg:hidden")}
        </NavbarItem>
      </NavbarContent>

      {/* Phones: keep the bar minimal; everything else is in the menu. */}
      <NavbarContent
        className="sm:hidden basis-1 items-center gap-1"
        justify="end"
      >
        <AuthButton />
        {menuToggle("")}
      </NavbarContent>

      <NavbarMenu
        className="gap-1 pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))] pt-4"
        motionProps={menuMotionProps}
      >
        <div className="mb-3 lg:hidden">{searchInput}</div>
        <ul className="flex flex-col gap-1">
          {siteLinks.map((item) => (
            <NavbarMenuItem key={item.href} isActive={item.active}>
              <NextLink
                aria-current={item.active ? "page" : undefined}
                className={clsx(
                  menuLinkClass,
                  item.active
                    ? "font-semibold text-primary"
                    : "text-foreground",
                )}
                href={`/${currentLang}${item.href}`}
                onClick={() => onNavigate(item.href)}
              >
                {item.label}
              </NextLink>
            </NavbarMenuItem>
          ))}
        </ul>

        {showLibrary && (
          <nav aria-labelledby="mobile-menu-library" className="mt-4">
            <p
              className="px-2 text-xs font-semibold uppercase tracking-wide text-default-500"
              id="mobile-menu-library"
            >
              {t("nav.myLibrary")}
            </p>
            <ul className="mt-1 flex flex-col gap-1">
              {libraryLinks.map((item) => (
                <NavbarMenuItem key={item.href} isActive={item.active}>
                  <NextLink
                    aria-current={item.active ? "page" : undefined}
                    className={clsx(
                      menuLinkClass,
                      item.active
                        ? "font-semibold text-primary"
                        : "text-foreground",
                    )}
                    href={`/${currentLang}${item.href}`}
                    onClick={() => onNavigate(item.href)}
                  >
                    {item.label}
                  </NextLink>
                </NavbarMenuItem>
              ))}
            </ul>
          </nav>
        )}

        {/* On phones the language and theme controls live here. */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4 sm:hidden">
          <div
            aria-label={t("language.switchLanguage")}
            className="flex gap-1 rounded-medium bg-default-100 p-1"
            role="group"
          >
            {languageCodes.map((code) => (
              <button
                key={code}
                aria-pressed={code === currentLang}
                className={clsx(
                  "min-h-11 rounded-small px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-focus",
                  code === currentLang
                    ? "bg-background font-semibold shadow-small"
                    : "text-default-600",
                )}
                lang={code}
                type="button"
                onClick={() => switchLanguage(code)}
              >
                {languages[code].name}
              </button>
            ))}
          </div>
          <ThemeSwitch className="p-3" />
        </div>
      </NavbarMenu>
    </HeroUINavbar>
  );
};
